import type { PropertyDto } from '../../services/api';
import { onLocaleChange, t, translatePropertyStatus, translatePropertyType } from '../../services/i18n';
import { getPropertyStatusBadgeClass } from './property-shared';
import './rc-property-details';

function esc(value: string): string {
  const span = document.createElement('span');
  span.textContent = value;
  return span.innerHTML;
}

function resolveReturnUrl(from: string | null): string {
  if (!from || !from.startsWith('/') || from.startsWith('//')) {
    return '/properties/';
  }

  return from;
}

export class RcPropertyDetailPage extends HTMLElement {
  private propertyId = '';
  private initialTab = '';
  private autoAction = '';
  private returnUrl = '/properties/';
  private property: PropertyDto | null = null;
  private localeCleanup?: () => void;

  connectedCallback(): void {
    const params = new URLSearchParams(window.location.search);
    this.propertyId = params.get('property') ?? '';
    this.initialTab = params.get('tab') ?? '';
    this.autoAction = params.get('autoAction') ?? '';
    this.returnUrl = resolveReturnUrl(params.get('from'));
    this.localeCleanup = onLocaleChange(() => this.renderHeader());
    this.addEventListener('property-details-loaded', this.handlePropertyLoaded as EventListener);
    this.render();
  }

  disconnectedCallback(): void {
    this.localeCleanup?.();
    this.removeEventListener('property-details-loaded', this.handlePropertyLoaded as EventListener);
  }

  private render(): void {
    this.innerHTML = `
      <section class="property-detail-page">
        <header class="page-header property-detail-page-header"></header>
        <div class="property-detail-page-body"></div>
      </section>
    `;

    this.renderHeader();
    this.renderBody();
  }

  private renderHeader(): void {
    const header = this.querySelector<HTMLElement>('.property-detail-page-header');
    if (!header) {
      return;
    }

    const heading = this.property
      ? esc(this.property.displayName || this.property.address)
      : t('property.details.pageTitle');
    const subtitle = this.property
      ? esc(translatePropertyType(this.property.type))
      : t('property.details.pageSubtitle');
    const badges = this.property
      ? `
        <div class="property-detail-page-badges">
          <span class="badge badge-info">${esc(translatePropertyType(this.property.type))}</span>
          <span class="badge ${getPropertyStatusBadgeClass(this.property.status)}">${esc(translatePropertyStatus(this.property.status))}</span>
        </div>
      `
      : '';

    header.innerHTML = `
      <div class="property-detail-page-heading">
        <a class="property-detail-backlink" href="${esc(this.returnUrl)}">${t('property.details.backToOverview')}</a>
        <div class="property-detail-page-title-row">
          <div class="property-detail-page-title-copy">
            <h1>${heading}</h1>
            <p class="property-detail-page-subtitle">${subtitle}</p>
          </div>
          ${badges}
        </div>
      </div>
    `;
  }

  private renderBody(): void {
    const body = this.querySelector<HTMLElement>('.property-detail-page-body');
    if (!body) {
      return;
    }

    if (!this.propertyId) {
      body.innerHTML = `<div class="empty-state">${t('property.details.missingSelection')}</div>`;
      return;
    }

    body.innerHTML = '';
    const details = document.createElement('rc-property-details');
    details.setAttribute('property-id', this.propertyId);
    if (this.initialTab) {
      details.setAttribute('initial-tab', this.initialTab);
    }
    if (this.autoAction) {
      details.setAttribute('auto-action', this.autoAction);
    }
    body.appendChild(details);
  }

  private handlePropertyLoaded = (event: CustomEvent<{ property: PropertyDto }>): void => {
    if (!event.detail?.property) {
      return;
    }

    this.property = event.detail.property;
    this.renderHeader();
  };
}

if (!customElements.get('rc-property-detail-page')) {
  customElements.define('rc-property-detail-page', RcPropertyDetailPage);
}
